/**
 * Persistent environment damage: bullet holes, sand pock marks and blast scorch.
 *
 * Cosmetic only, and deliberately *persistent* — a decal placed here stays for the whole
 * raid, so a corridor you have fought over reads as fought over. `clear()` is called on
 * `welcome` and on the way back to the hub; nothing else ever removes one.
 *
 * The one exception is **footprints**, which are the opposite of persistent: a trail has to
 * end or it is a map of everywhere you have ever been. They live in a fourth layer with a
 * per-instance alpha and a fade keyed to the number of foot plants since — see `footprint()`
 * and `FOOT_FADE_PLANTS`.
 *
 * ## Shape of the thing
 * One `InstancedMesh` per kind (hole / pock / scorch / foot), each with a preallocated
 * capacity and a ring buffer: the 257th bullet hole overwrites the first. That is four draw
 * calls for the entire feature, no matter how long the firefight runs. Three of the four do
 * no per-frame work at all — `onShot` / `scorch` each write one instance matrix and one
 * instance colour and nothing else. `update(dt)` exists only for the footprint fade, and it
 * walks the live window (at most sixteen prints), never the capacity.
 *
 * `instanceColor` is allocated in the constructor rather than on first use: it changes the
 * shader key, and the meshes must be complete before `precompileScene` walks the scene in
 * `main.ts` or the first decal of a raid would compile a pipeline mid-frame.
 *
 * Textures are procedural `CanvasTexture`s built once at boot — no external assets, and
 * the alpha channel does all the shaping, so a near-black decal over the hull darkens it
 * the way a scorch should while the sand pock's pale rim lifts the regolith.
 *
 * ## Orientation
 * The greybox world is axis-aligned boxes plus an optional heightfield, so the hit normal
 * is exact: the nearest box face the point is sitting on, or the finite-difference terrain
 * normal (`slopeAt`), or the flat ground. Points that match nothing at all are *dropped* —
 * `targetId === 0` also covers "hit nothing, ended at max range", and a decal hanging in
 * mid-air is a worse bug than a missing one. The "face the shooter" fallback is kept for
 * the case it is actually good for: a point near a box but not near any one face (an edge
 * or a corner), where no face normal is honest.
 */

import {
  CanvasTexture,
  Color,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  MeshBasicNodeMaterial,
  PlaneGeometry,
  Quaternion,
  SRGBColorSpace,
  Vector3,
  type Scene,
  type Texture,
} from 'three/webgpu';
import * as TSL from 'three/tsl';
import { heightAt, slopeAt, type Vec3, type World } from '@stillsun/shared';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;
/** Loose TSL aliases — same reason render/sky.ts uses them. */
const { attribute, texture, uv, vec2, mix, color } = TSL as unknown as Record<
  'attribute' | 'texture' | 'uv' | 'vec2' | 'mix' | 'color',
  (...args: AnyNode[]) => AnyNode
>;

/** Ring capacities. Holes are the only kind a firefight produces by the hundred. */
const CAP = { scorch: 32, foot: 64 } as const;
type Kind = keyof typeof CAP;
/** The three permanent combat marks, which share a geometry, a placement path and a ring. */
/**
 * Bullet holes and sand pocks went with hitscan: there is no `Ev.Shot` on the wire any more and
 * nothing left to place them from. What survives is the burn a blast leaves and the print a boot
 * leaves, which is two draws — inside the three `docs/PERF-BUDGET.md` allows for the whole field.
 */
type MarkKind = 'scorch';
const MARK_KINDS: readonly MarkKind[] = ['scorch'];

/** Decal diameters in metres. */
const HOLE_SIZE = 0.11;
const POCK_SIZE = 0.18;
/** The Verger's hazard panel burns a floor tile; a rail lance just chars a patch. */
const SCORCH_SIZE = 3;
const RAIL_SCORCH_SIZE = 0.8;

/** How far off the surface a decal floats. 1.5 cm beats z-fighting at every draw distance
 * we ship; `polygonOffset` below is the belt to this pair of braces. */
const LIFT = 0.015;

/** How close to a face a point has to be for that face's normal to be the honest one. */
const FACE_EPS = 0.12;
/** Slop band: near a surface, but no single face matched. Past this it is mid-air. */
const NEAR_EPS = 0.3;

/** Two scorches closer than this are the same burn; the second is dropped. */
const SCORCH_MERGE_M = 1;

/** Tints, multiplied per instance by a small brightness jitter. */
const HOLE_TINT = 0x9aa0a4;
const POCK_TINT = 0xd8c9a8;
const SCORCH_TINT = 0x6a625a;

// --- footprints -------------------------------------------------------------------------------
/**
 * A boot print, in metres. Longer than it is wide, and the two feet are planted `FOOT_STANCE`
 * either side of the path so a trail reads as a walk rather than as a tightrope.
 */
const FOOT_LENGTH = 0.3;
const FOOT_WIDTH = 0.12;
const FOOT_STANCE = 0.09;
const FOOT_TINT = 0x2a2620;

/**
 * How many further foot plants a print survives. A pace is one plant, and the brief is "fades
 * over ~8 paces", so sixteen footfalls.
 *
 * Counting *plants* rather than seconds is the whole point: a sprinter plants feet twice as fast
 * and covers twice the ground, so a wall-clock fade would leave them a trail twice as long as a
 * walker's. Sixteen plants is the same length of trail behind everybody.
 */
const FOOT_FADE_PLANTS = 16;
/**
 * …and the belt: a print also dies after this many seconds however few plants have landed since,
 * so a player who stops walking is not standing in their own footprints a minute later. Whichever
 * rule fires first wins.
 */
const FOOT_MAX_SEC = 12;
/**
 * Live prints never exceed `FOOT_FADE_PLANTS` (the plant rule retires one for every one placed),
 * so the per-frame fade walk is bounded by this and not by `CAP.foot` — which is deliberately
 * larger so the ring cursor can never lap a live slot.
 */
const FOOT_LIVE_MAX = FOOT_FADE_PLANTS;

// Module scratch — nothing in this file allocates once the meshes exist.
const _pos = new Vector3();
const _quat = new Quaternion();
const _roll = new Quaternion();
const _scale = new Vector3(1, 1, 1);
const _mat4 = new Matrix4();
const _col = new Color();
const _planeNormal = new Vector3(0, 0, 1);
const _zAxis = new Vector3(0, 0, 1);
const _normal = new Vector3();
const _dir = new Vector3();
const _fwd = new Vector3();
const _tan = new Vector3();
const _bit = new Vector3();
const _axisX = new Vector3();
const _axisY = new Vector3();

/** What `resolveSurface` found under a hit point. Mutated in place; never allocated. */
interface Surface {
  nx: number;
  ny: number;
  nz: number;
  /** Regolith (dune, apron, terrain) rather than hull metal — a paler, wider mark. */
  sand: boolean;
}
const _surf: Surface = { nx: 0, ny: 1, nz: 0, sand: true };

interface Layer {
  mesh: InstancedMesh;
  cap: number;
  /** Ring cursor: the slot the next decal of this kind lands in. */
  next: number;
  /** How many slots have ever been written (capped at `cap`), for the merge scan. */
  used: number;
  /** Centres of the live decals, for the scorch merge test. */
  cx: Float32Array;
  cy: Float32Array;
  cz: Float32Array;
}

/**
 * The one kind that fades, and so the one kind with per-frame state.
 *
 * `data` is `[fade, mirror]` per instance and *is* the buffer behind the GPU attribute, so the
 * fade walk writes straight into it with no copy. `tail`/`live` describe a window inside the ring
 * — never more than `FOOT_LIVE_MAX` wide — which is what keeps `update()` O(1) rather than
 * O(`CAP.foot`).
 */
interface FootLayer {
  mesh: InstancedMesh;
  geometry: PlaneGeometry;
  data: Float32Array;
  attr: InstancedBufferAttribute;
  /** Ring cursor: the slot the next print lands in. */
  next: number;
  /** Oldest live slot. */
  tail: number;
  /** How many slots between `tail` and `next` are live. */
  live: number;
  /** Monotonic count of every print ever placed. A print's age in paces is `plants - born[i]`. */
  plants: number;
  /** The value of `plants` when slot `i` was placed. */
  born: Float64Array;
  /** …and the value of `clock` (seconds since construction) when it was. */
  bornAt: Float64Array;
  /** Seconds since construction. Advanced by `update(dt)`; never reads wall time. */
  clock: number;
  /** True while the attribute still needs one more upload after the last print died. */
  dirty: boolean;
}

export class DecalField {
  private readonly scene: Scene;
  private readonly layers: Record<MarkKind, Layer>;
  private readonly foot: FootLayer;
  private readonly textures: Texture[] = [];
  private readonly materials: MeshBasicNodeMaterial[] = [];
  private readonly geometry = new PlaneGeometry(1, 1);

  constructor(scene: Scene) {
    this.scene = scene;
    this.layers = {
      scorch: this.makeLayer('scorch', scorchTexture()),
    };
    this.foot = this.makeFootLayer(bootTexture());
    // DevTools handle, the same shape as `__stillsunChars` in game/remote.ts: lets a probe
    // (or Chris) drop a mark by hand — `__stillsunDecals.scorch({x,y,z}, {boxes:[],groundY:0})`.
    (globalThis as { __stillsunDecals?: unknown }).__stillsunDecals = this;
  }

  // ------------------------------------------------------------------ placement

  /**
   * A blast burn on whatever surface `pos` is sitting on. The Verger's electrified floor
   * panel is the game's only true detonation-shaped event (see `main.ts`); the rail lance
   * above reuses the same layer at a quarter of the size.
   *
   * Repeated panels land on the same tile, so a burn within `SCORCH_MERGE_M` of a live one
   * is dropped rather than churning the 32-slot ring.
   */
  scorch(pos: Vec3, world: World, size = SCORCH_SIZE): void {
    if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)) return;
    // Straight down: a blast marks the floor it went off on.
    _dir.set(0, -1, 0);
    if (!resolveSurface(pos, world, _dir, _surf)) {
      // A hazard panel is placed *at* the floor, so a miss here means the position is a
      // little above it; drop it onto the ground rather than lose the mark.
      _surf.nx = 0;
      _surf.ny = 1;
      _surf.nz = 0;
      _surf.sand = true;
    }
    const layer = this.layers.scorch;
    for (let i = 0; i < layer.used; i++) {
      const dx = (layer.cx[i] ?? 0) - pos.x;
      const dy = (layer.cy[i] ?? 0) - pos.y;
      const dz = (layer.cz[i] ?? 0) - pos.z;
      if (dx * dx + dy * dy + dz * dz < SCORCH_MERGE_M * SCORCH_MERGE_M) return;
    }
    this.place('scorch', pos, _surf, size, SCORCH_TINT);
  }

  // ------------------------------------------------------------------ footprints

  /**
   * One foot plant. `yaw` is the walker's facing (yaw 0 faces -Z, the same convention the rest of
   * the client uses); `left` picks the mirrored sole and the side of the path the print sits on.
   * Fades out over ~8 paces — see `FOOT_FADE_PLANTS`.
   *
   * Retirement of the *previous* prints happens here rather than in `update()` on purpose: the
   * plant rule is the primary one, so if the caller never wires `update()` the trail still ends
   * after sixteen plants (prints pop out rather than fading) instead of accumulating forever.
   */
  footprint(pos: Vec3, yaw: number, world: World, left: boolean): void {
    if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)) return;
    const f = this.foot;

    // Straight down: a boot marks the floor it stands on. A miss means the caller handed us a
    // point a little above the ground (the usual case for a "feet" position), so take the ground.
    _dir.set(0, -1, 0);
    if (!resolveSurface(pos, world, _dir, _surf)) groundNormal(world, pos, _surf);

    f.plants += 1;
    this.retireFeet();

    _normal.set(_surf.nx, _surf.ny, _surf.nz).normalize();
    // Walk direction, flattened onto the surface, then a right vector from it. `b = t x n` makes
    // (b, t, n) right-handed, so the plane's +Y runs toe-forward and its +Z is the ground normal.
    _fwd.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    _tan.copy(_fwd).addScaledVector(_normal, -_fwd.dot(_normal));
    if (_tan.lengthSq() < 1e-8) {
      // Facing straight into a wall's normal — no honest forward. Any tangent will do.
      _tan.set(_normal.y, -_normal.x, 0);
      if (_tan.lengthSq() < 1e-8) _tan.set(1, 0, 0);
    }
    _tan.normalize();
    _bit.copy(_tan).cross(_normal).normalize();

    const slot = f.next;
    f.next = (f.next + 1) % CAP.foot;
    f.live += 1;
    if (f.live === 1) f.tail = slot;

    _axisX.copy(_bit).multiplyScalar(FOOT_WIDTH);
    _axisY.copy(_tan).multiplyScalar(FOOT_LENGTH);
    _mat4.makeBasis(_axisX, _axisY, _normal);
    const side = left ? -FOOT_STANCE : FOOT_STANCE;
    _mat4.setPosition(
      pos.x + _normal.x * LIFT + _bit.x * side,
      pos.y + _normal.y * LIFT + _bit.y * side,
      pos.z + _normal.z * LIFT + _bit.z * side,
    );
    f.mesh.setMatrixAt(slot, _mat4);
    f.mesh.instanceMatrix.needsUpdate = true;

    f.born[slot] = f.plants;
    f.bornAt[slot] = f.clock;
    f.data[slot * 2 + 0] = 1;
    // The mirrored sole is the same texture read with a flipped u — see `makeFootLayer`.
    f.data[slot * 2 + 1] = left ? 1 : 0;
    f.attr.needsUpdate = true;
    f.dirty = true;
  }

  /**
   * Advances the footprint fade. Called once a frame; everything else in this file is event
   * driven and does nothing here.
   *
   * The walk is over the live window only, which the plant rule caps at `FOOT_LIVE_MAX` — sixteen
   * iterations at the very most, whatever `CAP.foot` is. Nothing is touched at all once the last
   * print has gone.
   */
  update(dt: number): void {
    const f = this.foot;
    f.clock += dt;
    if (f.live === 0) {
      if (f.dirty) {
        f.attr.needsUpdate = true;
        f.dirty = false;
      }
      return;
    }
    this.retireFeet();
    for (let n = 0; n < f.live; n++) {
      const i = (f.tail + n) % CAP.foot;
      const byPlants = 1 - (f.plants - (f.born[i] ?? 0)) / FOOT_FADE_PLANTS;
      const byTime = 1 - (f.clock - (f.bornAt[i] ?? 0)) / FOOT_MAX_SEC;
      const k = Math.min(byPlants, byTime);
      // Smoothed so a print thins out rather than stepping down as each new plant lands.
      const e = k < 0 ? 0 : k > 1 ? 1 : k;
      f.data[i * 2 + 0] = e * e * (3 - 2 * e);
    }
    f.attr.needsUpdate = true;
    f.dirty = true;
  }

  /** Pops dead prints off the tail of the live window. Amortised O(1): one in, one out. */
  private retireFeet(): void {
    const f = this.foot;
    let removed = false;
    while (f.live > 0) {
      const i = f.tail;
      const agePlants = f.plants - (f.born[i] ?? 0);
      const ageSec = f.clock - (f.bornAt[i] ?? 0);
      if (agePlants < FOOT_FADE_PLANTS && ageSec < FOOT_MAX_SEC) break;
      f.data[i * 2 + 0] = 0;
      _mat4.makeScale(0, 0, 0);
      f.mesh.setMatrixAt(i, _mat4);
      f.tail = (f.tail + 1) % CAP.foot;
      f.live -= 1;
      removed = true;
    }
    // Only when a matrix actually moved: the fade walk rewrites `data` every frame but never
    // touches `instanceMatrix`, and re-uploading it for nothing is a kilobyte a frame of waste.
    if (removed) {
      f.mesh.instanceMatrix.needsUpdate = true;
      f.attr.needsUpdate = true;
    }
  }

  /** Wipes every decal. Called on `welcome` and on the way back to the hub. */
  clear(): void {
    for (const kind of MARK_KINDS) {
      const layer = this.layers[kind];
      _mat4.makeScale(0, 0, 0);
      for (let i = 0; i < layer.cap; i++) layer.mesh.setMatrixAt(i, _mat4);
      layer.mesh.instanceMatrix.needsUpdate = true;
      layer.next = 0;
      layer.used = 0;
    }
    const f = this.foot;
    _mat4.makeScale(0, 0, 0);
    for (let i = 0; i < CAP.foot; i++) {
      f.mesh.setMatrixAt(i, _mat4);
      f.data[i * 2 + 0] = 0;
    }
    f.mesh.instanceMatrix.needsUpdate = true;
    f.attr.needsUpdate = true;
    f.next = 0;
    f.tail = 0;
    f.live = 0;
    f.dirty = false;
  }

  dispose(): void {
    for (const kind of MARK_KINDS) {
      this.scene.remove(this.layers[kind].mesh);
      this.layers[kind].mesh.dispose();
    }
    this.scene.remove(this.foot.mesh);
    this.foot.mesh.dispose();
    this.foot.geometry.dispose();
    for (const m of this.materials) m.dispose();
    for (const t of this.textures) t.dispose();
    this.geometry.dispose();
  }

  // ------------------------------------------------------------------ internals

  private place(kind: MarkKind, p: Vec3, surf: Surface, size: number, tint: number): void {
    const layer = this.layers[kind];
    const i = layer.next;
    layer.next = (layer.next + 1) % layer.cap;
    if (layer.used < layer.cap) layer.used += 1;

    _normal.set(surf.nx, surf.ny, surf.nz);
    _pos.set(p.x + surf.nx * LIFT, p.y + surf.ny * LIFT, p.z + surf.nz * LIFT);
    // Align the plane's +Z with the surface normal, then spin it about that normal so no
    // two marks share a rotation — the cheapest way to stop 200 identical stamps reading
    // as a repeated texture.
    _quat.setFromUnitVectors(_planeNormal, _normal);
    _quat.multiply(_roll.setFromAxisAngle(_zAxis, Math.random() * Math.PI * 2));
    _scale.set(size, size, 1);
    _mat4.compose(_pos, _quat, _scale);
    layer.mesh.setMatrixAt(i, _mat4);
    layer.mesh.instanceMatrix.needsUpdate = true;

    _col.setHex(tint, SRGBColorSpace).multiplyScalar(0.78 + Math.random() * 0.35);
    layer.mesh.setColorAt(i, _col);
    if (layer.mesh.instanceColor) layer.mesh.instanceColor.needsUpdate = true;

    layer.cx[i] = p.x;
    layer.cy[i] = p.y;
    layer.cz[i] = p.z;
  }

  private makeLayer(kind: MarkKind, map: Texture): Layer {
    const cap = CAP[kind];
    const material = new MeshBasicNodeMaterial({
      map,
      transparent: true,
      depthWrite: false,
      // Decals are part of the world: they must be hidden by anything in front of them.
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    const mesh = new InstancedMesh(this.geometry, material, cap);
    mesh.name = `decals:${kind}`;
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    // Under the ability rings and floor patches, over the world.
    mesh.renderOrder = 3;
    // The instances are scattered across the whole map; the bounding sphere of the unit
    // plane at the origin would cull the lot the moment the origin left the frustum.
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    // Every slot starts as a zero-scale (invisible) instance, and `instanceColor` is
    // allocated here so the pipeline `precompileScene` builds is the one we render with.
    _mat4.makeScale(0, 0, 0);
    _col.setRGB(1, 1, 1);
    for (let i = 0; i < cap; i++) {
      mesh.setMatrixAt(i, _mat4);
      mesh.setColorAt(i, _col);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) {
      mesh.instanceColor.setUsage(DynamicDrawUsage);
      mesh.instanceColor.needsUpdate = true;
    }
    this.scene.add(mesh);
    this.textures.push(map);
    this.materials.push(material);

    return {
      mesh,
      cap,
      next: 0,
      used: 0,
      cx: new Float32Array(cap),
      cy: new Float32Array(cap),
      cz: new Float32Array(cap),
    };
  }

  /**
   * The footprint layer. One more `InstancedMesh`, so one more draw — it cannot share the other
   * three's geometry because an `InstancedBufferAttribute` lives on the *geometry*, and a 64-wide
   * one hung off the shared plane would be read out of range by the 256-instance hole layer.
   *
   * The per-instance attribute is `[fade, mirror]`. `fade` is the alpha, walked by `update()`.
   * `mirror` flips the sole's u in the shader, which is how the left boot exists without a second
   * texture — and, being a uniform-free per-instance read, without a second material or pipeline.
   */
  private makeFootLayer(map: Texture): FootLayer {
    const cap = CAP.foot;
    const geometry = new PlaneGeometry(1, 1);
    const data = new Float32Array(cap * 2);
    const attr = new InstancedBufferAttribute(data, 2);
    attr.setUsage(DynamicDrawUsage);
    geometry.setAttribute('instFoot', attr);

    const material = new MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    const inst = attribute('instFoot', 'vec2');
    const flippedU = mix(uv().x, uv().x.oneMinus(), inst.y);
    const tex = texture(map, vec2(flippedU, uv().y));
    material.colorNode = tex.rgb.mul(color(FOOT_TINT));
    material.opacityNode = tex.a.mul(inst.x);

    const mesh = new InstancedMesh(geometry, material, cap);
    mesh.name = 'decals:foot';
    mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    // Under the bullet marks and the ability rings, over the world.
    mesh.renderOrder = 2;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;

    // Zero-scale, zero-alpha instances from the start, so `precompileScene` compiles the pipeline
    // we actually render with rather than the first footstep of the raid doing it mid-frame.
    _mat4.makeScale(0, 0, 0);
    for (let i = 0; i < cap; i++) mesh.setMatrixAt(i, _mat4);
    mesh.instanceMatrix.needsUpdate = true;
    attr.needsUpdate = true;

    this.scene.add(mesh);
    this.textures.push(map);
    this.materials.push(material);

    return {
      mesh,
      geometry,
      data,
      attr,
      next: 0,
      tail: 0,
      live: 0,
      plants: 0,
      born: new Float64Array(cap),
      bornAt: new Float64Array(cap),
      clock: 0,
      dirty: false,
    };
  }
}

// ---------------------------------------------------------------------- surfaces

/**
 * Which surface `p` is sitting on, written into `out`. Returns false when the point is not
 * on anything — a shot that ended at max range in open air.
 *
 * A stricter sibling of `combat.ts`'s `approxNormal`, which answers "up" when it finds
 * nothing; a persistent decal needs to know the difference. It is also a better material
 * test than `main.ts`'s `surfaceAt`: that one only asks about box *tops* (it exists for
 * footsteps), so a wall would come back as regolith.
 */
function resolveSurface(p: Vec3, world: World, dir: Vector3, out: Surface): boolean {
  let bestD = Infinity;
  let bx = 0;
  let by = 0;
  let bz = 0;
  /** True when the best face belongs to a below-grade box — the ground apron, not hull. */
  let bestApron = false;
  let nearBox = false;

  for (const b of world.boxes) {
    if (
      p.x < b.min.x - NEAR_EPS ||
      p.x > b.max.x + NEAR_EPS ||
      p.y < b.min.y - NEAR_EPS ||
      p.y > b.max.y + NEAR_EPS ||
      p.z < b.min.z - NEAR_EPS ||
      p.z > b.max.z + NEAR_EPS
    ) {
      continue;
    }
    nearBox = true;
    // Below-grade geometry is the map's own ground apron (see `surfaceAt` in main.ts).
    const apron = b.min.y < 0;
    let d = Math.abs(p.x - b.min.x);
    if (d < bestD) {
      bestD = d;
      bx = -1;
      by = 0;
      bz = 0;
      bestApron = apron;
    }
    d = Math.abs(p.x - b.max.x);
    if (d < bestD) {
      bestD = d;
      bx = 1;
      by = 0;
      bz = 0;
      bestApron = apron;
    }
    d = Math.abs(p.y - b.min.y);
    if (d < bestD) {
      bestD = d;
      bx = 0;
      by = -1;
      bz = 0;
      bestApron = apron;
    }
    d = Math.abs(p.y - b.max.y);
    if (d < bestD) {
      bestD = d;
      bx = 0;
      by = 1;
      bz = 0;
      bestApron = apron;
    }
    d = Math.abs(p.z - b.min.z);
    if (d < bestD) {
      bestD = d;
      bx = 0;
      by = 0;
      bz = -1;
      bestApron = apron;
    }
    d = Math.abs(p.z - b.max.z);
    if (d < bestD) {
      bestD = d;
      bx = 0;
      by = 0;
      bz = 1;
      bestApron = apron;
    }
  }

  const groundY = world.terrain ? heightAt(world.terrain, p.x, p.z) : world.groundY;
  const dy = Math.abs(p.y - groundY);

  // A box face wins outright when the point is genuinely on it.
  if (bestD < FACE_EPS && bestD <= dy) {
    out.nx = bx;
    out.ny = by;
    out.nz = bz;
    out.sand = bestApron;
    return true;
  }
  if (dy < FACE_EPS) {
    groundNormal(world, p, out);
    return true;
  }
  // Slop band: still on something, just not cleanly on one face.
  if (bestD < NEAR_EPS) {
    out.nx = bx;
    out.ny = by;
    out.nz = bz;
    out.sand = bestApron;
    return true;
  }
  if (dy < NEAR_EPS) {
    groundNormal(world, p, out);
    return true;
  }
  // Inside a box's skin but near no face at all — an edge or a corner, where no face
  // normal is honest. Face the shooter instead.
  if (nearBox) {
    out.nx = -dir.x;
    out.ny = -dir.y;
    out.nz = -dir.z;
    out.sand = false;
    return true;
  }
  return false;
}

/** Terrain normal from the finite-difference slope, or straight up on a flat map. */
function groundNormal(world: World, p: Vec3, out: Surface): void {
  out.sand = true;
  if (!world.terrain) {
    out.nx = 0;
    out.ny = 1;
    out.nz = 0;
    return;
  }
  const s = slopeAt(world.terrain, p.x, p.z);
  const inv = 1 / Math.hypot(s.dx, 1, s.dz);
  out.nx = -s.dx * inv;
  out.ny = inv;
  out.nz = -s.dz * inv;
}

// ---------------------------------------------------------------------- textures

function canvas2d(size: number): CanvasRenderingContext2D | null {
  const el = document.createElement('canvas');
  el.width = size;
  el.height = size;
  return el.getContext('2d');
}

function finish(ctx: CanvasRenderingContext2D | null, fallback: number): Texture {
  const tex = new CanvasTexture(ctx ? ctx.canvas : blankCanvas(fallback));
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

/** A 2x2 grey square, for the browser that hands back no 2D context. */
function blankCanvas(alpha: number): HTMLCanvasElement {
  const el = document.createElement('canvas');
  el.width = 2;
  el.height = 2;
  const ctx = el.getContext('2d');
  if (ctx) {
    ctx.fillStyle = `rgba(20,18,16,${String(alpha)})`;
    ctx.fillRect(0, 0, 2, 2);
  }
  return el;
}

/** Bullet hole in hull plate: a black core with a scuffed, slightly brighter rim. */
function holeTexture(): Texture {
  const ctx = canvas2d(64);
  if (ctx) {
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(8,7,6,0.98)');
    g.addColorStop(0.34, 'rgba(14,12,10,0.9)');
    g.addColorStop(0.52, 'rgba(120,112,102,0.5)');
    g.addColorStop(0.78, 'rgba(90,84,76,0.16)');
    g.addColorStop(1, 'rgba(90,84,76,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    // A handful of chips off the rim so the stamp is not a perfect circle.
    for (let i = 0; i < 9; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 12 + Math.random() * 9;
      ctx.fillStyle = `rgba(10,9,8,${String(0.3 + Math.random() * 0.45)})`;
      ctx.beginPath();
      ctx.arc(32 + Math.cos(a) * r, 32 + Math.sin(a) * r, 1.5 + Math.random() * 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  return finish(ctx, 0.9);
}

/** Regolith pock: a shallow dark cup inside a pale ring of thrown dust. */
function pockTexture(): Texture {
  const ctx = canvas2d(64);
  if (ctx) {
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(42,35,28,0.72)');
    g.addColorStop(0.3, 'rgba(66,57,46,0.4)');
    g.addColorStop(0.52, 'rgba(216,200,172,0.26)');
    g.addColorStop(0.8, 'rgba(206,190,162,0.09)');
    g.addColorStop(1, 'rgba(206,190,162,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    // Grains flung clear of the crater.
    for (let i = 0; i < 22; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = 16 + Math.random() * 14;
      ctx.fillStyle = `rgba(226,212,186,${String(0.07 + Math.random() * 0.2)})`;
      ctx.beginPath();
      ctx.arc(
        32 + Math.cos(a) * r,
        32 + Math.sin(a) * r,
        0.6 + Math.random() * 1.6,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
  }
  return finish(ctx, 0.5);
}

/**
 * Boot sole: a heel pad and a wider forefoot pad, joined by a narrow waist, with tread bars
 * across both. 64 x 128 so the canvas has the boot's own aspect and the tread bars stay square
 * once the quad is scaled to `FOOT_WIDTH` x `FOOT_LENGTH`.
 *
 * v = 0 is the heel and v = 1 the toe, which matches the quad's +Y being the walk direction. Only
 * the right boot is drawn; the left one is this texture with its u flipped in the shader.
 */
function bootTexture(): Texture {
  const el = document.createElement('canvas');
  el.width = 64;
  el.height = 128;
  const ctx = el.getContext('2d');
  if (ctx) {
    // Canvas y grows downward, so the toe (v = 1) is drawn at the top.
    const pad = (cx: number, cy: number, rx: number, ry: number, a: number): void => {
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(rx, ry));
      g.addColorStop(0, `rgba(150,140,126,${String(a)})`);
      g.addColorStop(0.72, `rgba(150,140,126,${String(a * 0.8)})`);
      g.addColorStop(1, 'rgba(150,140,126,0)');
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(rx / Math.max(rx, ry), ry / Math.max(rx, ry));
      ctx.translate(-cx, -cy);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 128);
      ctx.restore();
    };
    // Forefoot (broad, offset a little to the outside), waist, heel.
    pad(34, 34, 24, 30, 0.82);
    pad(31, 70, 13, 18, 0.5);
    pad(30, 100, 20, 22, 0.78);

    // Tread: bars across the forefoot, a chevron block on the heel. Drawn as cut-outs so the
    // print has structure rather than reading as a smudge.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    for (let y = 14; y < 56; y += 9) ctx.fillRect(8, y, 48, 3.4);
    for (let y = 86; y < 118; y += 10) ctx.fillRect(10, y, 42, 3.6);
    ctx.fillRect(28, 60, 8, 24);
    ctx.globalCompositeOperation = 'source-over';
  }
  return finish(ctx, 0.6);
}

/** Blast scorch: soot, densest at the seat of the burn, with radiating streaks. */
function scorchTexture(): Texture {
  const ctx = canvas2d(128);
  if (ctx) {
    const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    g.addColorStop(0, 'rgba(6,5,4,0.92)');
    g.addColorStop(0.3, 'rgba(12,10,8,0.76)');
    g.addColorStop(0.6, 'rgba(22,18,15,0.42)');
    g.addColorStop(0.85, 'rgba(30,25,20,0.12)');
    g.addColorStop(1, 'rgba(30,25,20,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    ctx.lineCap = 'round';
    for (let i = 0; i < 18; i++) {
      const a = Math.random() * Math.PI * 2;
      const r0 = 22 + Math.random() * 12;
      const r1 = r0 + 12 + Math.random() * 32;
      ctx.strokeStyle = `rgba(8,7,6,${String(0.12 + Math.random() * 0.35)})`;
      ctx.lineWidth = 1.5 + Math.random() * 5;
      ctx.beginPath();
      ctx.moveTo(64 + Math.cos(a) * r0, 64 + Math.sin(a) * r0);
      ctx.lineTo(64 + Math.cos(a) * r1, 64 + Math.sin(a) * r1);
      ctx.stroke();
    }
  }
  return finish(ctx, 0.8);
}
